// The store owner's kiosk theme colours, made safe to hand to MUI.
//
// TKT-2026-107229 (kiosk sibling of the buylist fix). The dashboard's Kiosk
// Configuration colour boxes were free text and sortswift-api stored whatever
// was typed. This app hands those values to MUI (theme/kioskDesignTokens.js
// calls getLuminance/lighten/darken/alpha on them, then App.js calls
// createTheme), and MUI throws on a colour it cannot parse: `ffffff` with no
// '#', `RGB(1,2,3)`, `red`. One typo blanked the whole kiosk, and because
// KioskThemeContext caches the theme in localStorage the device stayed blank
// even after the network went away.
//
// THE RULE. sortswift (dashboard), sortswift-api, sortswift-buylist and this
// repo carry the same rule; change them together or not at all. This copy is a
// READER, like sortswift-buylist's src/utils/themeColour.js, so it differs
// from the writers in two deliberate ways: an invalid value becomes '' (use the
// stock kiosk colour) instead of being refused, and step 3 lower-cases the
// function name, because MUI's decomposeColor only knows 'rgb'/'rgba'/'hsl'/
// 'hsla' and throws on 'RGB(1,2,3)'.
//
//   normaliseThemeColour(value)
//     1. non-string, or empty after trim         -> ''  (use the stock colour)
//     2. remove ALL whitespace; if it then matches
//          /^#?([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i
//                                               -> '#' + the hex digits, case preserved
//     3. else if the TRIMMED original matches
//          /^(rgb|rgba|hsl|hsla)\([^()]*\)$/i   -> the trimmed original, with the
//                                                  function name lower-cased
//     4. else                                   -> ''  (unparseable; use stock)
//
//   normaliseKioskTheme(overrides)
//     A shallow copy in which each of the seven kiosk colour keys that is
//     PRESENT is passed through normaliseThemeColour. Every other key
//     (fontFamily, storeName, defaultColorMode, salesTaxRate, ...) is left
//     untouched, and an absent key stays absent. A non-object (null,
//     undefined, an array) is returned as is.
//
// '' already means "not set" to every reader here (kioskDesignTokens' has()),
// so an unparseable value falls back to the stock colour instead of reaching MUI.

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const FUNCTIONAL_RE = /^(rgb|rgba|hsl|hsla)\([^()]*\)$/i;

// The colour fields of kioskConfig.kioskTheme (sortswift-api src/models/User.js).
// defaultColorMode is NOT a colour ('light' / 'dark' / 'system').
export const KIOSK_THEME_COLOUR_KEYS = Object.freeze([
    'primaryColor',
    'secondaryColor',
    'successColor',
    'backgroundColor',
    'paperColor',
    'textPrimary',
    'textSecondary',
]);

export function normaliseThemeColour(value) {
    if (typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (trimmed === '') return '';

    const hex = trimmed.replace(/\s+/g, '').match(HEX_RE);
    if (hex) return `#${hex[1]}`;

    const fn = trimmed.match(FUNCTIONAL_RE);
    if (fn) return fn[1].toLowerCase() + trimmed.slice(fn[1].length);

    return '';
}

export function normaliseKioskTheme(overrides) {
    if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) {
        return overrides;
    }
    let out = null;
    for (const key of KIOSK_THEME_COLOUR_KEYS) {
        if (!(key in overrides)) continue;
        const normalised = normaliseThemeColour(overrides[key]);
        if (normalised !== overrides[key]) {
            if (!out) out = { ...overrides };
            out[key] = normalised;
        }
    }
    // Same object back when nothing changed, so a clean theme keeps its identity
    // (App.js keys createTheme on it; TKT-2026-105056).
    return out || overrides;
}
